/*
 * File: VendorCell.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 10 Jan 2022 2:59:44 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { Form, Select, Spin, Tooltip } from 'antd';
import { useField } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReduxState } from 'redux/rootReducer';
import { useGetVendorsQuery } from 'redux/services/useSimonSoundman';
import { useDebouncedCallback } from 'use-debounce';
import { BlockLoader } from './BlockLoader';

interface VendorCellProps {
  id: SalesOrderPayloadModel['lines'][number]['id'];
  vendorId: SalesOrderPayloadModel['lines'][number]['vendorId'];
  index: number;
}

const styles: InlineStylesModel = {
  formItem: {
    width: '100%'
  }
};

export const VendorCell = ({ index, id, vendorId }: VendorCellProps): JSX.Element => {
  const [x, _, { setValue }] = useField(`lines.${index}.vendorId`);
  const form = Form.useFormInstance();

  const { selectedCompanyDataAreaId } = useSelector((state: ReduxState) => state.salesOrders);

  const [inputValue, _setInputValue] = useState<string | undefined>(undefined);
  const [offset, _setOffset] = useState(0);

  const { pathname } = useLocation();

  /* ****************** API Hooks ****************** */
  const { data: vendorResponse, isLoading, isFetching, isError } = useGetVendorsQuery({ dataAreaId: selectedCompanyDataAreaId ?? '', offset, vendorAccountContains: inputValue });

  /* ****************** JSX / Variables ****************** */
  const vendorsOptions = useMemo(() => vendorResponse?.data?.map((vendor) => ({ value: vendor.vendorAccountNumber, label: `${vendor.vendorAccountNumber} - ${vendor.vendorName}` })), [vendorResponse]);
  const isDraftForm = pathname.includes('/drafts/');
  const isPosted = pathname.includes('/posted');
  /* ****************** Functions ****************** */
  // Handle delay of searching data
  const handleSearch = useDebouncedCallback((value: string | undefined) => {
    _setOffset(0);
    _setInputValue(value);
  }, 1000);

  const handleClear = (): void => {
    _setInputValue(undefined);
    _setOffset(0);
    setValue('');
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    const target = event.currentTarget;

    if (!isLoading && !isFetching && target.scrollTop + target.offsetHeight === target.scrollHeight && vendorResponse && vendorResponse?.totalCount - offset > 25) {
      _setOffset(offset + 25);
    }
  };

  const handleSelect = (value: string): void => {
    form.setFieldValue('vendorId', value);
  };

  const dropDownRender = (menu: JSX.Element): JSX.Element => {
    return (
      <div>
        {menu}
        {(isLoading || isFetching) && <Spin indicator={<BlockLoader direction="loader loader--slideUp" />} spinning />}
      </div>
    );
  };

  const isInvalid = useMemo(
    (): boolean => ((id.includes('import') || isDraftForm) && (vendorResponse?.data.length === 0 || isError) ? true : false),
    [isDraftForm, isError, id, vendorResponse?.data.length]
  );

  const errorMessage = useCallback((): string => {
    if ((id.includes('import') || isDraftForm) && isError) {
      return 'Unable to valid vendor on import';
    }

    if ((id.includes('import') || isDraftForm) && vendorResponse?.data.length === 0) {
      return `Invalid vendor account id`;
    }

    return 'Unable to report the issue';
  }, [isDraftForm, isError, id, vendorResponse?.data.length]);

  /* ****************** Use Effects ****************** */

  /* ****************** Render ****************** */
  return (
    <Tooltip title={isInvalid ? errorMessage() : undefined}>
      <Select
        disabled={isPosted}
        allowClear
        dropdownMatchSelectWidth={false}
        dropdownRender={dropDownRender}
        filterOption={false}
        loading={isLoading || isFetching}
        onClear={handleClear}
        onPopupScroll={handleScroll}
        onSearch={handleSearch}
        onChange={handleSelect}
        options={vendorsOptions}
        value={form.getFieldValue('vendorId')}
        showSearch
        style={styles.formItem}
      />
    </Tooltip>
  );
};
